import React, {useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import {useTranslation} from 'react-i18next';
import type {RagThreadHandoffStatus} from 'crqlar-react-common/dist/models/ai/ragThread.model';

const TEAM_ORANGE = '#ed6c02';
const TEAM_ORANGE_TEXT = '#b25400';
const ERROR_RED = '#c62828';
// the widget's positive colour, shared with the thumbs-up in the message actions
const SUCCESS_GREEN = '#388e3c';
const SUCCESS_GREEN_TEXT = '#2e7d32';

// A conversation this long is rarely still about its first question — and the assistant re-reads the
// whole history every turn, so a fresh chat answers better as well as reading better.
const LONG_CHAT_MESSAGE_COUNT = 25;

/** Shared shell for the notices that sit between the transcript and the composer. */
const NoticeBar: React.FC<{
    icon?: React.ReactNode;
    text: string;
    color: string;
    bgcolor?: string;
    action?: React.ReactNode;
}> = ({icon, text, color, bgcolor = 'transparent', action}) => (
    <Box role="status" sx={{px: 1.5, pt: 0.5}}>
        <Stack
            direction="row"
            alignItems="flex-start"
            spacing={0.75}
            sx={{px: 1.25, py: 0.75, borderRadius: '8px', bgcolor}}
        >
            {icon}
            <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 12, fontWeight: 500, color}}>
                    {text}
                </Typography>
                {action}
            </Box>
        </Stack>
    </Box>
);

/** A notice's secondary action: a text button under the copy, quiet enough not to compete with it. */
const NoticeAction: React.FC<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void | Promise<void>;
}> = ({label, icon, onClick}) => {
    const [busy, setBusy] = useState(false);

    const handleClick = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onClick();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button
            size="small"
            onClick={handleClick}
            disabled={busy}
            startIcon={icon}
            sx={{
                mt: 0.25,
                ml: -0.75,
                px: 0.75,
                py: 0.125,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'none',
                color: 'inherit',
                '&:hover': {bgcolor: 'rgba(0, 0, 0, 0.04)'},
            }}
        >
            {label}
        </Button>
    );
};

/**
 * The end of a support conversation, and the only handoff state that is an outcome rather than a
 * status — so it gets a card instead of a strip: the team closed this, and the customer should see
 * that at a glance rather than read it off a grey line above the composer.
 *
 * The sub-line carries the server's actual rule (sending here reopens the conversation with the
 * team), which is why the button offers the other road: a clean chat with the assistant.
 */
const ResolvedPanel: React.FC<{onStartNewChat?: () => void}> = ({onStartNewChat}) => {
    const {t} = useTranslation();

    return (
        <Box role="status" sx={{px: 1.5, pt: 0.75}}>
            <Stack
                direction="row"
                spacing={1.25}
                sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: 'rgba(56, 142, 60, 0.08)',
                    border: '1px solid rgba(56, 142, 60, 0.24)',
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        border: '1px solid rgba(56, 142, 60, 0.24)',
                        color: SUCCESS_GREEN,
                    }}
                >
                    <CheckCircleOutlineIcon sx={{fontSize: 19}}/>
                </Box>

                <Box sx={{minWidth: 0}}>
                    <Typography sx={{fontSize: 14, fontWeight: 700, color: SUCCESS_GREEN_TEXT}}>
                        {t('Resolved')}
                    </Typography>
                    <Typography sx={{fontSize: 12.5, lineHeight: 1.45, color: 'text.secondary', mt: 0.25}}>
                        {t('Have a follow-up question? Reply here. For a new topic, start a new chat to keep the conversation short and focused.')}
                    </Typography>

                    {onStartNewChat && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onStartNewChat}
                            startIcon={<EditNoteIcon sx={{fontSize: 16}}/>}
                            sx={{
                                mt: 1.25,
                                px: 1.25,
                                fontSize: 12.5,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {boxShadow: 'none'},
                            }}
                        >
                            {t('Start a new chat')}
                        </Button>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

/**
 * Who the customer is talking to, taken straight from the thread's handoff status — the server's
 * routing switch, refreshed by every send and poll. It sits directly above the composer rather than
 * in the transcript because it describes where the *next* message goes, and because the states flip
 * underneath a conversation (a team reply moves WAITING → ACTIVE) instead of being events in it.
 *
 * The input is never disabled: sending is what reopens a resolved conversation and what retries a
 * failed handoff, so every state's copy points back at the composer. While the conversation is still
 * open with the team, the action is the way out without losing it — ending the handoff leaves the
 * same thread with the assistant, the team's answers still in its history. RESOLVED is the one state
 * where that is moot (nobody is holding the conversation any more), so it offers a fresh chat.
 */
const AssistantHandoffBanner: React.FC<{
    status?: RagThreadHandoffStatus;
    onBackToAssistant?: () => void | Promise<void>;
    onStartNewChat?: () => void;
}> = ({status, onBackToAssistant, onStartNewChat}) => {
    const {t} = useTranslation();

    if (!status || status === 'NONE') return null;
    if (status === 'RESOLVED') return <ResolvedPanel onStartNewChat={onStartNewChat}/>;

    const teamNotices = {
        WAITING: {
            icon: <SupportAgentOutlinedIcon sx={{fontSize: 16, color: TEAM_ORANGE, mt: '1px'}}/>,
            text: t('The team has been notified — replies will appear here.'),
            color: TEAM_ORANGE_TEXT,
            bgcolor: 'rgba(237, 108, 2, 0.09)',
        },
        ACTIVE: {
            icon: <SupportAgentOutlinedIcon sx={{fontSize: 16, color: TEAM_ORANGE, mt: '1px'}}/>,
            text: t("You're chatting with the Crqlar team."),
            color: TEAM_ORANGE_TEXT,
            bgcolor: 'rgba(237, 108, 2, 0.09)',
        },
        FAILED: {
            icon: <ErrorOutlineIcon sx={{fontSize: 16, color: ERROR_RED, mt: '1px'}}/>,
            text: t("We couldn't reach the team — sending a message retries."),
            color: ERROR_RED,
            bgcolor: 'rgba(198, 40, 40, 0.08)',
        },
    };
    const {icon, text, color, bgcolor} = teamNotices[status];

    return (
        <NoticeBar
            icon={icon}
            text={text}
            color={color}
            bgcolor={bgcolor}
            // Offered in every state that still has the conversation, FAILED included — there it is
            // "give up on the team and ask the assistant instead", and on WAITING it doubles as
            // cancelling the request.
            action={onBackToAssistant && (
                <NoticeAction
                    label={t('Back to the assistant')}
                    icon={<SmartToyOutlinedIcon sx={{fontSize: 15}}/>}
                    onClick={onBackToAssistant}
                />
            )}
        />
    );
};

/**
 * Nudge out of a conversation that has outgrown its question. Advice, not a limit — nothing stops
 * the customer carrying on here — so it stays quiet: no fill, no icon, and AI mode only (a
 * conversation with the team already says its own piece).
 */
export const AssistantLongChatHint: React.FC<{
    messageCount: number;
    onStartNewChat?: () => void;
}> = ({messageCount, onStartNewChat}) => {
    const {t} = useTranslation();

    if (messageCount < LONG_CHAT_MESSAGE_COUNT || !onStartNewChat) return null;

    return (
        <NoticeBar
            text={t('This chat is getting long — a new one keeps answers sharper for a new topic.')}
            color="text.secondary"
            action={(
                <NoticeAction
                    label={t('Start a new chat')}
                    icon={<EditNoteIcon sx={{fontSize: 15}}/>}
                    onClick={onStartNewChat}
                />
            )}
        />
    );
};

export default AssistantHandoffBanner;
